import { dequal as deepEqual } from 'dequal/lite';
import get from 'lodash/fp/get';
import type { FieldPathValue, Path } from 'react-hook-form';
import type { Replace } from 'type-fest';
import type { ConfigOverrideKey, TagName } from '#dn-types/host.ts';
import { mapOverridesToForm } from '#screens/hosts/form/advanced/mappers/mapOverridesToForm.ts';
import type { HostFormData } from '#screens/hosts/form/host-form-schema.ts';
import type { OverridePlusSource } from '#screens/hosts/hooks/useOverrideMaps.ts';

type Props<T extends Path<HostFormData>> = {
  name: T;
  formValue: FieldPathValue<HostFormData, T>;
  overrideMap: Map<string, OverridePlusSource>;
};

/** {@link isValueFromTag} takes an overrideMap and config override name and formValue, and returns whether the two config override values match, i.e. the tag is providing the config override. */
export function checkIsValueFromTag<T extends Path<HostFormData>>({ name, formValue, overrideMap }: Props<T>) {
  let tagName: TagName | undefined;
  let isValueFromTag = false;
  let overrideKey: ConfigOverrideKey | undefined;

  // Look to see if this field has a tag that can set its config
  if (name.startsWith('configOverrides.')) {
    overrideKey = name.replace('configOverrides.', '') as Replace<
      Extract<typeof name, `configOverrides.${ConfigOverrideKey}`>,
      'configOverrides.',
      ''
    >;
    const override = overrideMap.get(overrideKey);
    if (override?.source !== 'host') {
      tagName = override?.source;
    }

    // Convert tag provided value to form style config override
    const tagProvidedValue = get(overrideKey, mapOverridesToForm(override ? [override.value] : [])) as unknown;

    if (deepEqual(tagProvidedValue, formValue)) {
      isValueFromTag = true;
    }
  }

  return {
    tagName,
    isValueFromTag,
    overrideKey,
  };
}
